"use client";

/**
 * Pencere durumu — kaynak-referans `features/window/state/Window.ts` (mobx) paritesi.
 *
 * kaynak-referans ile aynı desen: `makeAutoObservable` sınıf singleton'ı. Observable
 * alanlar → sınıf alanları, action metodları → sınıf metodları, getter'lar →
 * metod/getter.
 *
 * kaynak-referans constructor'daki `initListeners()` (focus/blur/visibilitychange/resize)
 * burada modül-kapsamı boot bloğunda: SSR'da `window`/`document` yok, bu yüzden
 * `typeof window` korumasıyla ve yalnızca bir kez bağlanır. Başlangıç değerleri
 * de aynı korumayla hesaplanır (SSR'da odaklı/görünür varsayılır, boyut 0x0 →
 * ilk resize'da düzelir).
 *
 * DEFERRED (bilinçli): kaynak-referans `WindowCommands.focused()` odak değişiminde
 * `GuildReadState.handleWindowFocus` / `Idle.recordActivity` /
 * `Notification.handleWindowFocus` yan etkilerini tetikler — bu store'ların
 * TEBHOS'ta karşılığı yok (idle takibi `usePresence`, odak aboneliği
 * `FocusManager` üzerinden yürüyor), bu yüzden komut katmanı taşınmadı.
 */

import { makeAutoObservable } from "mobx";
import { Logger } from "@app/features/platform/utils/Logger";

const logger = new Logger("Window");

/** Pencerenin iç (viewport) boyutu. */
export interface WindowSize {
  width: number;
  height: number;
}

const getWindowSize = (): WindowSize =>
  typeof window !== "undefined"
    ? { width: window.innerWidth, height: window.innerHeight }
    : { width: 0, height: 0 };

const getInitialFocused = (): boolean =>
  typeof document !== "undefined" ? document.hasFocus() : true;

const getInitialVisible = (): boolean =>
  typeof document !== "undefined" ? !document.hidden : true;

/** Bu sekme/pencere örneği için benzersiz kimlik üretir. */
function generateWindowId(): string {
  return `window-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

class Window_ {
  /** Pencere/sekme odakta mı. */
  focused = getInitialFocused();
  /** Sekme görünür mü (`document.hidden` tersi). */
  visible = getInitialVisible();
  /** Güncel viewport boyutu. */
  windowSize: WindowSize = getWindowSize();
  /** Bu pencere örneğinin benzersiz kimliği. */
  windowId = generateWindowId();
  /** Son odaklanma zamanı (epoch ms). */
  lastFocusedAt = Date.now();
  /** Store'un oluşturulma zamanı (epoch ms). */
  createdAt = Date.now();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // ── Aksiyonlar (kaynak-referans action metodları) ────────────────────────────

  setFocused(focused: boolean): void {
    if (this.focused === focused) return;
    logger.debug(`Pencere odağı değişti: ${focused}`);
    this.focused = focused;
    if (focused) {
      this.lastFocusedAt = Date.now();
      // kaynak-referans WindowCommands.focused() → GuildReadState.handleWindowFocus
      // (BİLDİRİM-GÖSTERGELERİ 1-1, 2026-07-19): odak dönüşünde süresi dolmuş
      // mute'lar vb. için rozet/pill toplulaştırması yeniden değerlendirilir.
      // Lazy import: modül-yükleme sırasında döngüsel bağımlılık kurulmasın.
      void import("@app/features/guild/state/GuildReadState")
        .then((m) => m.default.handleWindowFocus())
        .catch(() => {});
    }
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return;
    logger.debug(`Pencere görünürlüğü değişti: ${visible}`);
    this.visible = visible;
  }

  updateWindowSize(): void {
    const windowSize = getWindowSize();
    logger.debug(`Pencere yeniden boyutlandı: ${windowSize.width}x${windowSize.height}`);
    // YENİ referans: reaction(() => WindowStore.windowSize) tüketicileri
    // referans karşılaştırmasıyla tetiklenir.
    this.windowSize = windowSize;
  }

  // ── Getter'lar (kaynak-referans getter paritesi) ─────────────────────────────

  isFocused(): boolean {
    return this.focused;
  }

  isVisible(): boolean {
    return this.visible;
  }

  getWindowSize(): WindowSize {
    return this.windowSize;
  }
}

/** Pencere durumu singleton'ı (kaynak-referans `WindowStore` paritesi). */
export const WindowStore = new Window_();

// Boot: pencere olay dinleyicilerini bağla (bir kez, yalnızca tarayıcıda) —
// kaynak-referans constructor `initListeners()` paritesi.
if (typeof window !== "undefined") {
  window.addEventListener("focus", () => WindowStore.setFocused(true));
  window.addEventListener("blur", () => WindowStore.setFocused(false));
  document.addEventListener("visibilitychange", () => {
    WindowStore.setVisible(!document.hidden);
  });
  window.addEventListener("resize", () => WindowStore.updateWindowSize());
}
